// Browser Extensions for Researchers in 2026: Building a Real Research Workstation
//
// SEO roundup post. Primary target: "browser extensions for researchers".
// Long-tail: "research browser extensions", "academic browser extensions",
// "browser extensions PhD students", "AI research assistant browser".
// Ported from marketing-blackmount/content/articles/best-browser-extensions-researchers.md
// (2026-02-21), condensed and reframed. Pricing reflects each tool's public
// pricing at time of writing; Blackmount facts: Pro $0.99/mo, free Guest Mode
// (offline, no account), Chrome/Firefox/Edge/Brave/Vivaldi/Opera + iPhone.

import React from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link as MuiLink,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const AUTHORS = ['Mehrdad Shirangi'];
const PUBLISHED_AT = '2026-08-12';
const UPDATED_AT = '2026-08-12';

const meta = {
  slug: 'browser-extensions-for-researchers',
  title: 'Browser Extensions for Researchers in 2026: Building a Real Research Workstation',
  description: 'The browser extensions researchers actually need in 2026 — session memory, citation capture, paper access, annotation, and AI literature search — grouped by the job each one does.',
  publishedAt: PUBLISHED_AT,
  updatedAt: UPDATED_AT,
  authors: AUTHORS,
  tags: ['browser extensions for researchers', 'research tools', 'academic workflow', 'literature review', 'PhD students'],
  readingTime: '8 min',
  ogImage: '/blackmount-logo.jpg',
};

function Section({ id, title, children }) {
  return (
    <Box component="section" id={id} sx={{ mt: 5 }}>
      <Typography variant="h4" component="h2" fontWeight={700} sx={{ mb: 2, scrollMarginTop: 96 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75, fontSize: '1.05rem' }}>
      {children}
    </Typography>
  );
}

function Body() {
  return (
    <>
      <P>
        Research happens in the browser now. You read papers in tabs, chase citations across
        PubMed and Google Scholar, annotate PDFs inline, and run half a literature review
        without ever opening a desktop app. The problem is that browsers were built for casual
        reading, not for multi-week projects that span hundreds of tabs and dozens of sources.
        One crash, one restart, one switch to the laptop, and the working context is gone.
      </P>
      <P>
        This guide is organized by the job each extension does rather than by a ranked list,
        because researchers rarely need a single winner — they need one good tool per layer.
        Nothing here is crowned best overall. Pick the layers where your workflow actually
        leaks.
      </P>

      <Section id="sessions" title="Session memory: Blackmount (that's us)">
        <P>
          <MuiLink component={RouterLink} to="/">Blackmount</MuiLink> handles the layer most
          research stacks ignore. Reference managers capture individual items — a paper, a
          citation, a highlight. Blackmount captures the whole working state: every tab open
          at a given moment, plus the notes and voice recordings you attach to explain why
          those tabs were open. Save a session on the office desktop, restore it on the laptop
          at home.
        </P>
        <P>
          The voice note is the part researchers tend to keep. Forty tabs deep in a review and
          about to step away, you record ten seconds: &quot;These five are the transformer
          architecture papers for the methods section — the third one has the figure I need.&quot;
          Weeks later the session comes back with that context attached instead of a wall of
          anonymous titles. Saved sessions are searchable, including AI chats you have captured
          from ChatGPT, Claude, Gemini and others, which matters more every year as chat
          threads become part of the research trail.
        </P>
        <P>
          It runs on Chrome, Firefox, Edge, Brave, Vivaldi, and Opera, plus iPhone. Guest Mode
          is free, works offline, and needs no account; Pro is $0.99/month and adds cloud sync
          across devices. The honest limits: Blackmount is not a reference manager and not a
          PDF annotator. It will not generate a bibliography or let you highlight inside a
          paper. If you work in short bursts on one machine, you probably do not need it. If
          you routinely reopen the same twenty tabs on a different computer, see{' '}
          <MuiLink component={RouterLink} to="/blog/save-tabs-open-another-computer">
            how cross-device tab restore works
          </MuiLink>.
        </P>
      </Section>

      <Section id="citations" title="Citations: Zotero Connector, Mendeley Web Importer">
        <P>
          <MuiLink href="https://www.zotero.org/" target="_blank" rel="noopener noreferrer">Zotero Connector</MuiLink>{' '}
          is the reference layer most researchers should start with. It detects citable
          resources on a page — journal articles, books, patents, news — and saves them to your
          library in one click, pulling title, authors, journal, DOI, and date automatically,
          and grabbing the full-text PDF when it is available. Zotero itself is free and open
          source; paid cloud storage starts around $20/year once the 300 MB free tier fills up,
          which happens fast if you save PDFs. Word, Google Docs, and LibreOffice integration
          means citations flow through to the manuscript without retyping.
        </P>
        <P>
          <MuiLink href="https://www.mendeley.com/" target="_blank" rel="noopener noreferrer">Mendeley Web Importer</MuiLink>{' '}
          does much the same job with tighter Elsevier integration (ScienceDirect, Scopus),
          which is a real advantage in fields that live in those databases. Free tier includes
          2 GB; premium starts around $4.99/month. No Safari support. If you have no
          institutional pull toward Elsevier, Zotero's open-source ecosystem is the easier
          long-term bet.
        </P>
      </Section>

      <Section id="access" title="Paper access: Unpaywall">
        <P>
          <MuiLink href="https://unpaywall.org/" target="_blank" rel="noopener noreferrer">Unpaywall</MuiLink>{' '}
          is free, tiny, and worth installing before anything else on this list. When you land
          on a paywalled article, it searches an index of over 20 million legal open-access
          full texts and, if a version exists in a repository, preprint archive, or on the
          publisher's own site, drops a green tab on the edge of your screen linking straight
          to the PDF. Everything it surfaces is authorized green open access, not piracy.
          Coverage depends on whether someone deposited a copy, and it is Chrome and Firefox
          only.
        </P>
      </Section>

      <Section id="annotation" title="Annotation and highlighting: Hypothesis, Glasp, Liner">
        <P>
          <MuiLink href="https://web.hypothes.is/" target="_blank" rel="noopener noreferrer">Hypothesis</MuiLink>{' '}
          is the strongest choice when annotation is a group activity — reading groups, peer
          review, seminar work. Annotations can be private, public, or scoped to a group, and
          they support rich formatting including LaTeX. Two things to know: outside Chromium
          browsers you are on a bookmarklet, and annotations default to public until you change
          the setting.
        </P>
        <P>
          <MuiLink href="https://glasp.co/" target="_blank" rel="noopener noreferrer">Glasp</MuiLink>{' '}
          (free, Pro around $10/month billed annually) adds an AI layer over your highlights
          across pages, PDFs, and YouTube, and shows what other readers highlighted on the same
          page. The social-by-default posture does not suit unpublished or sensitive work.{' '}
          <MuiLink href="https://getliner.com/" target="_blank" rel="noopener noreferrer">Liner</MuiLink>{' '}
          offers a similar highlight-plus-AI-copilot experience on Chrome and Edge, but at
          roughly $16.67/month billed annually it is hard to justify unless the copilot
          genuinely replaces a separate AI subscription.
        </P>
      </Section>

      <Section id="clipping" title="Clipping and bookmarking: Notion Web Clipper, Raindrop.io">
        <P>
          <MuiLink href="https://www.notion.com/web-clipper" target="_blank" rel="noopener noreferrer">Notion Web Clipper</MuiLink>{' '}
          is the right call if Notion is already your knowledge base — you file a page directly
          into the correct database with properties set at capture time. It does not extract
          academic metadata the way Zotero does, and adopting Notion solely for the clipper is
          not worth it.
        </P>
        <P>
          <MuiLink href="https://raindrop.io/" target="_blank" rel="noopener noreferrer">Raindrop.io</MuiLink>{' '}
          (free, Pro around $3.54/month) is a genuinely good bookmark manager: nested
          collections, tags, previews, and full-text search across saved pages on Pro, which is
          how you find the paper you saved three months ago and tagged badly. It saves
          individual URLs, not sessions, and offers no citation export. If you came here after
          Pocket shut down, the{' '}
          <MuiLink component={RouterLink} to="/blog/pocket-alternatives-2026">
            Pocket alternatives comparison
          </MuiLink>{' '}
          covers this category in more depth.
        </P>
      </Section>

      <Section id="pdf" title="PDF reading: Google Scholar PDF Reader, Scholarcy">
        <P>
          <MuiLink href="https://chromewebstore.google.com/detail/google-scholar-pdf-reader/dahenjhkoodjbpjheillcadbppiidmhp" target="_blank" rel="noopener noreferrer">Google Scholar PDF Reader</MuiLink>{' '}
          is free and makes in-browser paper reading meaningfully better: a generated outline
          for navigation, in-text citations you can preview without leaving the page, jump-to-figure
          links, highlighting saved to your Scholar library, and proper dark and night modes.
          Chromium browsers only, and it is a reading tool rather than a reference manager.
        </P>
        <P>
          <MuiLink href="https://www.scholarcy.com/" target="_blank" rel="noopener noreferrer">Scholarcy</MuiLink>{' '}
          turns a paper into summary flashcards with extracted study details and links to open-access
          versions of its references — useful for triage during a wide review. The free
          extension has usage limits, and its summaries are AI-generated: fine for deciding what
          to read, never a substitute for reading what you cite.
        </P>
      </Section>

      <Section id="ai" title="AI literature search: Elicit, Semantic Scholar">
        <P>
          <MuiLink href="https://elicit.com/" target="_blank" rel="noopener noreferrer">Elicit</MuiLink>{' '}
          is a web app rather than an extension, but it earns a place here because it does the
          part of a systematic review that is pure labor: searching across more than 125 million
          papers, filtering by criteria, and extracting comparable data points across studies
          into a table. Free tier is a one-time credit allowance; paid plans run about
          $10-12/month. Verify every extracted value against the source before it reaches a
          manuscript.
        </P>
        <P>
          <MuiLink href="https://www.semanticscholar.org/" target="_blank" rel="noopener noreferrer">Semantic Scholar</MuiLink>{' '}
          is free, and its extension is a thin but frictionless bridge: highlight a title,
          keyword, or author anywhere and search it instantly, then follow the citation network,
          save to your library, or read the TLDR summary. The depth is in the platform — research
          feeds that learn from your library — more than in the extension itself.
        </P>
      </Section>

      <Section id="comparison-table" title="Quick comparison">
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small" sx={{ minWidth: 720 }} aria-label="Comparison of browser extensions for researchers">
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell><strong>Tool</strong></TableCell>
                <TableCell><strong>Layer</strong></TableCell>
                <TableCell><strong>Price</strong></TableCell>
                <TableCell align="center"><strong>Cross-device sync</strong></TableCell>
                <TableCell align="center"><strong>AI features</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'primary.light', '& td': { fontWeight: 500 } }}><TableCell>Blackmount</TableCell><TableCell>Session memory</TableCell><TableCell>Free / $0.99 mo</TableCell><TableCell align="center"><strong>Yes</strong></TableCell><TableCell align="center">Yes</TableCell></TableRow>
              <TableRow><TableCell>Zotero Connector</TableCell><TableCell>Citations</TableCell><TableCell>Free (paid storage)</TableCell><TableCell align="center">Via Zotero</TableCell><TableCell align="center">Via plugins</TableCell></TableRow>
              <TableRow><TableCell>Unpaywall</TableCell><TableCell>Paper access</TableCell><TableCell>Free</TableCell><TableCell align="center">—</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>Hypothesis</TableCell><TableCell>Annotation</TableCell><TableCell>Free / team plans</TableCell><TableCell align="center">Yes</TableCell><TableCell align="center">—</TableCell></TableRow>
              <TableRow><TableCell>Raindrop.io</TableCell><TableCell>Bookmarking</TableCell><TableCell>Free / ~$3.54 mo</TableCell><TableCell align="center">Yes</TableCell><TableCell align="center">Some</TableCell></TableRow>
              <TableRow><TableCell>Google Scholar PDF Reader</TableCell><TableCell>PDF reading</TableCell><TableCell>Free</TableCell><TableCell align="center">Via Scholar</TableCell><TableCell align="center">Yes</TableCell></TableRow>
              <TableRow><TableCell>Elicit</TableCell><TableCell>Literature search</TableCell><TableCell>Free tier / ~$10 mo</TableCell><TableCell align="center">Yes (web app)</TableCell><TableCell align="center">Yes</TableCell></TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section id="stacks" title="Pick by workflow">
        <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, lineHeight: 1.7 } }}>
          <li><strong>Writing a dissertation</strong> → Zotero Connector for citations, Elicit for the review, Google Scholar PDF Reader for reading, Blackmount for keeping each chapter's tab context separate.</li>
          <li><strong>Collaborative or seminar work</strong> → Zotero shared libraries plus Hypothesis groups.</li>
          <li><strong>Triaging a large literature</strong> → Elicit and Semantic Scholar for discovery, Scholarcy for fast screening.</li>
          <li><strong>Spending nothing</strong> → Zotero Connector, Unpaywall, Hypothesis, Semantic Scholar, Google Scholar PDF Reader, and Blackmount in Guest Mode.</li>
          <li><strong>Research that spans devices</strong> → any citation manager you like, plus something that restores the whole session, not just the links.</li>
        </Box>
      </Section>

      <Divider sx={{ my: 5 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pricing and browser support change often. Spotted an inaccuracy, or a tool we should
        add? Email <MuiLink href="mailto:support@blackmount.ai">support@blackmount.ai</MuiLink>{' '}
        — we update this post as tools ship new versions.
      </Typography>
    </>
  );
}

export default { ...meta, render: Body };
